/**
 * Shipping rate calculator — admin port of the storefront's
 * `useShippingEstimate` composable. Same Google Sheet, same zone lookup,
 * so a B2B quote never contradicts what the customer sees on elnaf.fi.
 *
 * Sheet layout:
 *   Countries: [name, fedexZone, upsZone, dhlZone]
 *   Fedex/UPS/DHL: [zone, kg, price]
 *
 * Billable weight = ceil(totalKg), minimum 1 kg.
 */

const SHEET_ID = '1N1ySpQlucixn8tSPIWmHaLaFpCrGvnmE_jT4zNaDpxk';
const API_KEY = 'AIzaSyB3SjC9O7TcdZxOZF5bUnmv5Zcod9jBixw';
const CACHE_KEY = 'elnaf_shipping_data_admin';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

let dataPromise = null;
let DATA = { countries: [], fedex: [], ups: [], dhl: [] };

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.ts || Date.now() - d.ts > CACHE_TTL_MS) return null;
    return d;
  } catch { return null; }
}

function writeCache(d) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...d, ts: Date.now() })); } catch { /* quota */ }
}

/** Fetch (or reuse) the shipping sheet. Resolves to true on success. */
export function loadShippingData() {
  if (dataPromise) return dataPromise;

  const cached = readCache();
  if (cached) {
    DATA = { countries: cached.countries, fedex: cached.fedex, ups: cached.ups, dhl: cached.dhl };
    dataPromise = Promise.resolve(true);
    return dataPromise;
  }

  dataPromise = (async () => {
    try {
      const sheet = (name, cols) =>
        fetch(`https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${name}!A:${cols}?key=${API_KEY}`)
          .then((r) => r.json());
      const [c, f, u, d] = await Promise.all([
        sheet('Countries', 'D'), sheet('Fedex', 'C'), sheet('UPS', 'C'), sheet('DHL', 'C'),
      ]);
      DATA = {
        countries: c.values?.slice(1) || [],
        fedex: f.values?.slice(1) || [],
        ups: u.values?.slice(1) || [],
        dhl: d.values?.slice(1) || [],
      };
      writeCache(DATA);
      return DATA.countries.length > 0;
    } catch {
      return false;
    }
  })();
  return dataPromise;
}

/** Country names exactly as they appear in the sheet (for the picker). */
export function listCountries() {
  return DATA.countries.map((r) => r[0]).filter(Boolean);
}

export const isShippingReady = () => DATA.countries.length > 0;

/** Same multi-strategy match as the storefront ("Finland", "Finland - FI", "FI"). */
function findCountryRow(countryName) {
  if (!countryName) return null;
  const nameLC = String(countryName).toLowerCase();

  let row = DATA.countries.find((c) => c[0] === countryName);
  if (!row) row = DATA.countries.find((c) => c[0]?.toLowerCase() === nameLC);
  if (!row) {
    row = DATA.countries.find((c) => {
      const parts = (c[0] || '').split(/\s*-\s*/).map((p) => p.toLowerCase().trim());
      return parts.includes(nameLC);
    });
  }
  return row || null;
}

/** Billable weight: round up to the next whole kg, never below 1. */
export function billableKg(totalKg) {
  const w = parseFloat(String(totalKg ?? 0).replace(',', '.')) || 0;
  return Math.max(1, Math.ceil(w));
}

/**
 * @returns {{status:'ok'|'no_route'|'no_data', kg?:number, country?:string,
 *            options?:Array<{carrier:string, price:number, currency:string}>}}
 */
export function calculateShipping(countryName, totalKg) {
  if (!DATA.countries.length) return { status: 'no_data', options: [] };
  const row = findCountryRow(countryName);
  if (!row) return { status: 'no_route', country: countryName, options: [] };

  const [, fedexZone, upsZone, dhlZone] = row;
  const kg = billableKg(totalKg);

  const findPrice = (table, zone) => {
    if (!zone) return null;
    const m = table.find((r) => r[0] === zone && parseInt(r[1], 10) === kg);
    return m ? parseFloat(m[2]) : null;
  };

  // All three rate sheets are in EUR — the storefront renders every carrier
  // with a "€" (CheckoutView, ProductDetailView, ProductCard), so the admin
  // must read them the same way.
  const options = [
    { carrier: 'FedEx', price: findPrice(DATA.fedex, fedexZone), currency: 'EUR' },
    { carrier: 'UPS',   price: findPrice(DATA.ups,   upsZone),   currency: 'EUR' },
    { carrier: 'DHL',   price: findPrice(DATA.dhl,   dhlZone),   currency: 'EUR' },
  ].filter((o) => o.price !== null && !Number.isNaN(o.price) && o.price > 0);

  if (!options.length) return { status: 'no_route', country: countryName, kg, options: [] };

  options.sort((a, b) => a.price - b.price);
  return { status: 'ok', country: countryName, kg, options };
}
